import { TranslateService } from '@ngx-translate/core';

/**
 * Weather indicator configuration
 */
export interface WeatherIndicatorOption {
  label: string;
  id: string;
  unit: string;
}

/**
 * Manager for weather indicator operations
 * Handles indicator options and value extraction
 */
export class ChartIndicatorManager {
  private translate: TranslateService;

  constructor(translate: TranslateService) {
    this.translate = translate;
  }

  /**
   * Get all available weather indicator options
   */
  getIndicatorOptions(): WeatherIndicatorOption[] {
    return [
      { label: this.translate.instant('TemperatureAverage'), id: 'TemperatureAverage', unit: '°C' },
      { label: this.translate.instant('Fallout'), id: 'Fallout', unit: 'mm' },
      { label: this.translate.instant('Humidity'), id: 'Humidity', unit: '%' },
      { label: this.translate.instant('WIND_SPEED'), id: 'WindSpeed', unit: 'm/s' },
      { label: this.translate.instant('SolarRadiationInfluence'), id: 'SolarRadiationInfluence', unit: 'kcal/m²' },
      { label: this.translate.instant('Pressure'), id: 'Pressure', unit: 'hPa' },
      { label: this.translate.instant('TemperatureMin'), id: 'TemperatureMin', unit: '°C' },
      { label: this.translate.instant('TemperatureMax'), id: 'TemperatureMax', unit: '°C' }
    ];
  }

  /**
   * Get indicator options filtered by data source
   */
  getFilteredIndicatorOptions(dataSource: string): WeatherIndicatorOption[] {
    const allOptions = this.getIndicatorOptions();

    if (dataSource === 'ugm') {
      return allOptions.filter(option => option.id === 'TemperatureAverage');
    }

    return allOptions;
  }

  /**
   * Get unit for a specific indicator
   */
  getIndicatorUnit(indicator: string): string {
    const units: { [key: string]: string } = {
      'temperature': '°C',
      'temperatureaverage': '°C',
      'fallout': 'mm',
      'precipitation': 'mm',
      'humidity': '%',
      'windspeed': 'm/s',
      'wind_speed': 'm/s',
      'solarradiation': 'kcal/m²',
      'solarradiationinfluence': 'kcal/m²',
      'pressure': 'hPa',
      'temperaturemin': '°C',
      'temperaturemax': '°C'
    };
    return units[indicator.toLowerCase()] || '';
  }

  /**
   * Extract indicator value from weather data item
   */
  extractIndicatorValue(data: any, indicator: string): number {
    if (!data) return 0;

    const indicatorLower = indicator.toLowerCase();

    switch (indicatorLower) {
      case 'temperature':
      case 'temperatureaverage':
        return data.temperatureAverage ?? 0;

      case 'precipitation':
      case 'fallout':
        return data.fallout ?? 0;

      case 'humidity':
        return data.humidity ?? 0;

      case 'windspeed':
      case 'wind_speed':
        return data.windSpeed ?? 0;

      case 'solarradiation':
      case 'solar_radiation':
      case 'solarradiationinfluence':
        // Convert W/m² to kcal/m²/day (1 W/m² = 20.64 kcal/m²/day)
        const valueInWatts = data.solarRadiationInfluence ?? 0;
        return Math.round(valueInWatts * 20.64);

      case 'pressure':
        return data.pressure ?? 0;

      case 'temperaturemin':
        return data.temperatureMin ?? 0;

      case 'temperaturemax':
        return data.temperatureMax ?? 0;

      case 'temperaturemintotal':
        return data.temperatureMinTotal ?? 0;

      case 'temperaturemaxtotal':
        return data.temperatureMaxTotal ?? 0;

      case 'windderection':
      case 'winddirection':
        return data.windDerection ?? 0;

      default:
        // Try to get the property directly
        return data[indicator] ?? data[indicatorLower] ?? 0;
    }
  }


  /**
   * Get default indicator for a data source
   */
  getDefaultIndicator(dataSource: string): string {
    if (dataSource === 'ugm') {
      return 'TemperatureAverage';
    }
    return 'TemperatureAverage';
  }
}
